import { ListChecks, Plus, Trash2 } from "lucide-react";
import { useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Field, Input, NativeSelect } from "@ncp/ui";
import type { BuilderRef } from "../workspaces/api";
import type { AgentBody, AgentBuilderOptions, OnApproveAction } from "./api";

type Patch = (p: Partial<AgentBody>) => void;
const TYPES = ["record.create", "letter.send", "process.start"] as const;
/** The setting holding the field → value map of each action type. */
const MAP_KEY: Record<string, string> = { "record.create": "fields", "letter.send": "values", "process.start": "intake" };
const PLACEHOLDERS = ["caseNumber", "title", "applicantName", "intake.field", "draft.title", "draft.summary", "draft.content", "output.field", "reviewer", "note", "today"];
const REF_KEY: Record<string, string> = { "record.create": "recordType", "letter.send": "template", "process.start": "service" };

/** What runs once a reviewer approves the agent's draft: create a record, send a letter, start another process – values from {{placeholders}}. */
export function OnApproveCard({ body, patch, readOnly, options }: { body: AgentBody; patch: Patch; readOnly: boolean; options?: AgentBuilderOptions }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const actions = body.onApproveActions ?? [];
  const set = (next: OnApproveAction[]) => patch({ onApproveActions: next });
  const setAt = (i: number, p: Partial<OnApproveAction>) => set(actions.map((a, j) => (j === i ? { ...a, ...p } : a)));
  const refs = (type: string): BuilderRef[] => (type === "record.create" ? options?.recordTypes : type === "letter.send" ? options?.letterTemplates : options?.services) ?? [];
  const label = (r: BuilderRef) => (locale === "ar" && r.nameAr ? r.nameAr : r.name);

  return (
    <Card data-testid="on-approve">
      <CardHeader className="flex-row flex-wrap items-start justify-between gap-2">
        <div>
          <CardTitle className="flex items-center gap-2"><ListChecks className="size-4 text-aegold-700" /> {t("admin:agents.onApprove.title")}</CardTitle>
          <CardDescription>{t("admin:agents.onApprove.intro")}</CardDescription>
        </div>
        {!readOnly ? <Button data-testid="on-approve-add" size="sm" variant="outline" disabled={actions.length >= 5} onClick={() => set([...actions, { type: "letter.send" }])}><Plus /> {t("admin:agents.onApprove.add")}</Button> : null}
      </CardHeader>
      <CardContent className="space-y-3">
        {actions.length === 0 ? <p className="rounded-xl bg-sand-50 p-3 text-xs text-muted-foreground">{t("admin:agents.onApprove.none")}</p> : null}
        {actions.map((a, i) => {
          const mapKey = MAP_KEY[a.type] ?? "fields";
          const refKey = REF_KEY[a.type] ?? "recordType";
          const map = (a[mapKey] && typeof a[mapKey] === "object" ? a[mapKey] : {}) as Record<string, string>;
          const entries = Object.entries(map);
          const setMap = (next: [string, string][]) => setAt(i, { [mapKey]: Object.fromEntries(next) });
          return (
            <div key={i} className="space-y-2 rounded-xl border border-sand-200 p-3">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="default" size="sm">{i + 1}</Badge>
                <NativeSelect data-testid={`on-approve-type-${i}`} value={a.type} disabled={readOnly} className="h-9 min-w-40 flex-1" onChange={(e) => set(actions.map((x, j) => (j === i ? { type: e.target.value } : x)))}>
                  {TYPES.map((ty) => <option key={ty} value={ty}>{t(`admin:agents.onApprove.types.${ty.replace(".", "_")}`)}</option>)}
                </NativeSelect>
                <NativeSelect data-testid={`on-approve-ref-${i}`} value={(a[refKey] as string) ?? ""} disabled={readOnly} className="h-9 min-w-48 flex-1" onChange={(e) => setAt(i, { [refKey]: e.target.value })}>
                  <option value="">{t(`admin:agents.onApprove.choose.${a.type.replace(".", "_")}`)}</option>
                  {refs(a.type).map((r) => <option key={r.key} value={r.key}>{label(r)}</option>)}
                </NativeSelect>
                {!readOnly ? <Button size="icon-sm" variant="ghost" aria-label={t("admin:agents.steps.remove")} onClick={() => set(actions.filter((_, j) => j !== i))}><Trash2 /></Button> : null}
              </div>
              {a.type === "process.start" ? (
                <Field label={t("admin:agents.onApprove.processTitle")}><Input value={(a.title as string) ?? ""} readOnly={readOnly} placeholder="Follow-up to {{caseNumber}}" onChange={(e) => setAt(i, { title: e.target.value || undefined })} /></Field>
              ) : null}
              <div className="space-y-1.5">
                <p className="text-xs font-medium">{t(`admin:agents.onApprove.map.${a.type.replace(".", "_")}`)}</p>
                {entries.map(([k, v], j) => (
                  <div key={j} className="grid grid-cols-[1fr_1.6fr_auto] gap-2">
                    <Input value={k} readOnly={readOnly} dir="ltr" className="font-mono text-xs" aria-label={t("admin:agents.onApprove.field")} onChange={(e) => setMap(entries.map((x, n) => (n === j ? [e.target.value, x[1]] : x)))} />
                    <Input value={v} readOnly={readOnly} dir="ltr" className="font-mono text-xs" aria-label={t("admin:agents.onApprove.value")} placeholder="{{output.amount}}" onChange={(e) => setMap(entries.map((x, n) => (n === j ? [x[0], e.target.value] : x)))} />
                    {!readOnly ? <Button size="icon-sm" variant="ghost" aria-label={t("admin:agents.steps.remove")} onClick={() => setMap(entries.filter((_, n) => n !== j))}><Trash2 /></Button> : <span />}
                  </div>
                ))}
                {!readOnly ? <Button size="sm" variant="ghost" onClick={() => setMap([...entries, [`field${entries.length + 1}`, ""]])}><Plus /> {t("admin:agents.onApprove.addValue")}</Button> : null}
              </div>
            </div>
          );
        })}
        <p className="text-[11px] text-muted-foreground">
          {t("admin:agents.onApprove.placeholders")} {PLACEHOLDERS.map((p) => <code key={p} className="me-1 font-mono" dir="ltr">{`{{${p}}}`}</code>)}
        </p>
      </CardContent>
    </Card>
  );
}
